import { computed, ref } from 'vue';

import type { SelectModel } from '@/components/forms/FormSelect.vue';
import { toSelectModel } from '@/utils/conversion';

interface LazyLoadEvent {
  first: number;
  last: number;
}

export interface PaginatedResponse<T> {
  page?: number;
  limit?: number;
  count?: number;
  pages?: number;
  data?: T[];
}

export interface LazySelectFetchOptions {
  page: number;
  limit: number;
  searchString?: string | null;
}

interface UseLazySelectOptions<T extends { id: string | number; name: string }> {
  fetchPagedData: () => (
    options: LazySelectFetchOptions,
  ) => Promise<PaginatedResponse<T> | undefined> | undefined;
  fetchSingleItem?: (id: string) => Promise<T | null | undefined>;
  itemsPerPage?: number;
}

export function useLazySelect<T extends { id: string | number; name: string }>(
  options: UseLazySelectOptions<T>,
) {
  const itemsPerPage = ref(options.itemsPerPage ?? 50);
  const items = ref<SelectModel[]>([]);
  const loading = ref(false);
  const totalRecords = ref(0);
  const currentPage = ref(0);
  const searchString = ref<string | null>(null);
  const loadingInitialValues = ref<Set<string>>(new Set()); // Track which IDs are currently being loaded

  // Track if we've loaded all pages
  const allItemsLoaded = computed(() => {
    if (totalRecords.value === 0) return false;
    return items.value.length >= totalRecords.value;
  });

  /**
   * Lazy load callback - called when user scrolls through the list
   */
  const onLazyLoad = async (event: LazyLoadEvent) => {
    // Prevent multiple simultaneous loads and Check if all items are loaded
    if (loading.value || allItemsLoaded.value) {
      return;
    }

    // Check if we're approaching the end of loaded items
    // Load next page when we've scrolled past 80% of what's loaded
    const threshold = items.value.length * 0.8;

    // Load when we've scrolled past 80% of loaded items
    if (event.last < threshold) {
      return;
    }

    // Calculate next page to load
    const nextPage = currentPage.value + 1;

    loading.value = true;

    try {
      const response = await options.fetchPagedData()({
        page: nextPage,
        limit: itemsPerPage.value,
        searchString: searchString.value,
      });

      if (response && response.data) {
        // Map new items
        const mappedItems = toSelectModel(response.data);

        // Update totalRecords
        totalRecords.value = response.count ?? 0;

        // Append new items to the existing array
        items.value = [...items.value, ...mappedItems];

        // Update current page
        currentPage.value = nextPage;
      }
    } finally {
      loading.value = false;
    }
  };

  /**
   * Load initial batch of items
   */
  const loadInitialItems = async () => {
    loading.value = true;

    try {
      const response = await options.fetchPagedData()({
        page: 1,
        limit: itemsPerPage.value,
        searchString: searchString.value,
      });

      if (response && response.data) {
        const mappedItems = toSelectModel(response.data);
        totalRecords.value = response.count ?? 0;

        // Set initial items
        items.value = mappedItems;
        currentPage.value = 1;
      }
    } finally {
      loading.value = false;
    }
  };

  /**
   * Load a specific item by ID if not already in items
   */
  const loadInitialValue = async (initialValueId: string | undefined) => {
    if (!initialValueId) return null;

    // Check if we're already loading this ID
    if (loadingInitialValues.value.has(initialValueId)) {
      return null;
    }

    // Check if initial value is already in items
    const existingItem = items.value.find((item) => item.id === initialValueId);
    if (existingItem) {
      return existingItem;
    }

    // If fetchSingleItem function is provided, fetch it individually
    if (options.fetchSingleItem) {
      try {
        // Mark as loading
        loadingInitialValues.value.add(initialValueId);

        const item = await options.fetchSingleItem(initialValueId);
        if (item) {
          // Double-check it wasn't added while we were fetching
          const stillMissing = !items.value.find((i) => i.id === initialValueId);
          if (stillMissing) {
            // Map and prepend it to items
            const mappedItem = toSelectModel([item])[0];
            items.value = [mappedItem, ...items.value];
            return mappedItem;
          } else {
            return items.value.find((i) => i.id === initialValueId) || null;
          }
        }
      } finally {
        // Remove from loading set
        loadingInitialValues.value.delete(initialValueId);
      }
    }

    return null;
  };

  /**
   * Handle search/filter - resets the list and reloads with search string
   */
  const handleSearch = async (search: string | null) => {
    searchString.value = search;

    // Reset state and reload
    items.value = [];
    currentPage.value = 0;
    totalRecords.value = 0;

    await loadInitialItems();
  };

  /**
   * Reset all state
   */
  const reset = () => {
    items.value = [];
    loading.value = false;
    totalRecords.value = 0;
    currentPage.value = 0;
    searchString.value = null;
    loadingInitialValues.value.clear();
  };

  return {
    // State
    items,
    loading,
    totalRecords,
    allItemsLoaded,
    searchString,

    // Methods
    onLazyLoad,
    loadInitialItems,
    loadInitialValue,
    handleSearch,
    reset,
  };
}
